import {
  Box3,
  Camera,
  MathUtils,
  Object3D,
  OrthographicCamera,
  PerspectiveCamera,
  Sphere,
  Vector3,
} from "three";
import { CameraInterface } from "../Engine";

export function isPerspectiveViewCamera(
  camera?: { type?: string } | null,
): boolean {
  return camera?.type === "PerspectiveCamera";
}

/**
 * Bounding-sphere reduction shared by every fit/frame helper here.
 */
export function boundsOf(
  object: Object3D | { center: Vector3; radius: number },
): { center: Vector3; radius: number } {
  if (object instanceof Object3D) {
    const box = boxFromObjectExcludingFitIgnored(object);
    if (box.isEmpty()) {
      return { center: new Vector3(), radius: 1 };
    }
    const sphere = box.getBoundingSphere(new Sphere());
    return { center: sphere.center.clone(), radius: Math.max(sphere.radius, 1e-3) };
  }
  return { center: object.center.clone(), radius: object.radius };
}

/** Fit hesabına hiç girmemeli (ör. zemin grid). */
export function markExcludeFromFit(object: Object3D): void {
  object.userData.excludeFromFit = true;
  object.traverse((child) => {
    child.userData.excludeFromFit = true;
  });
}

function boxFromObjectExcludingFitIgnored(object: Object3D): Box3 {
  const box = new Box3();
  object.updateWorldMatrix(true, true);

  const expand = (node: Object3D) => {
    if (node.userData?.excludeFromFit) return;
    const mesh = node as import("three").Mesh;
    if (mesh.isMesh && mesh.geometry) {
      const geom = mesh.geometry;
      if (!geom.boundingBox) geom.computeBoundingBox();
      if (geom.boundingBox && !geom.boundingBox.isEmpty()) {
        const local = geom.boundingBox.clone();
        local.applyMatrix4(mesh.matrixWorld);
        box.union(local);
      }
    }
    for (const child of node.children) expand(child);
  };

  expand(object);
  return box;
}

/**
 * How the camera distance that "covers" a bounding sphere is derived.
 *
 * - `min-fov` — correct: vertical and horizontal FOV are computed separately
 *   (horizontal from the aspect ratio) and the tighter one drives the distance.
 *   This is what `viewer/ViewerEntity.frameModel` has always done.
 * - `legacy-aspect` — historical formula `aspect * fov` (an FOV scaled by the
 *   aspect ratio, which is not a real angle). It is *not* equivalent to
 *   `min-fov` and generally frames wider. Configurator camera presets and the
 *   `renderSnapshot` outputs were visually tuned against it, so it is kept
 *   verbatim rather than "fixed" — changing it moves every stored product
 *   snapshot. New code should pass `min-fov`.
 */
export type FitDistanceMode = "min-fov" | "legacy-aspect";

/**
 * Single source of truth for the fit distance. Previously this expression was
 * copy-pasted in `fitObjectToPersCamera`, `getCameraPresetFromBox` and
 * `ViewerEntity.frameModel` — two of them agreeing with each other and the
 * third not.
 */
export function computeFitDistance(
  radius: number,
  fovDeg: number,
  aspect: number,
  mode: FitDistanceMode = "min-fov",
): number {
  const safeAspect = aspect && Number.isFinite(aspect) ? aspect : 1;

  if (mode === "legacy-aspect") {
    const fovRad = safeAspect * fovDeg * (Math.PI / 180);
    return radius / Math.sin(fovRad * 0.5);
  }

  const vFov = MathUtils.degToRad(fovDeg);
  const hFov = 2 * Math.atan(Math.tan(vFov / 2) * safeAspect);
  const minFov = Math.min(vFov, hFov);
  return radius / Math.sin(minFov / 2);
}

export interface FrameObjectOptions {
  /** Direction from target to camera. Default: 3/4 view (0.4, 0.25, 1). */
  viewDir?: Vector3;
  /** Extra breathing room around the object. Default 1 (tight fit). */
  padding?: number;
  /** `maxZoom` is `distance * this`. Default 2. */
  maxZoomFactor?: number;
  /** See {@link FitDistanceMode}. Default `min-fov`. */
  mode?: FitDistanceMode;
}

export interface FrameObjectResult {
  position: Vector3;
  target: Vector3;
  radius: number;
  distance: number;
  /** Cannot get closer than the bounding radius (no clipping into the model). */
  minZoom: number;
  maxZoom: number;
}

const DEFAULT_VIEW_DIR = new Vector3(0.4, 0.25, 1).normalize();

/**
 * Canonical "frame this object" computation. Pure — it does not touch the
 * camera; callers apply the result to whichever camera/orbit they own.
 */
export function frameObject(
  object: Object3D | { center: Vector3; radius: number },
  camera: { fov: number; aspect: number },
  options: FrameObjectOptions = {},
): FrameObjectResult {
  const { center, radius } = boundsOf(object);
  const padding = options.padding ?? 1;
  const maxZoomFactor = options.maxZoomFactor ?? 2;

  const distance =
    computeFitDistance(radius, camera.fov, camera.aspect, options.mode) *
    padding;

  const viewDir = (options.viewDir ?? DEFAULT_VIEW_DIR).clone().normalize();
  const position = viewDir.multiplyScalar(distance).add(center);

  return {
    position,
    target: center,
    radius,
    distance,
    minZoom: radius,
    maxZoom: distance * maxZoomFactor,
  };
}

export function fitObjectToCamerInterface(
  object: Object3D | { center: Vector3; radius: number },
  camera: CameraInterface
) {
  return fitObjectToCamera(object, camera.instance);
}

export function fitObjectToCamera(
  object: Object3D | { center: Vector3; radius: number },
  camera: Camera | PerspectiveCamera | OrthographicCamera | CameraInterface
) {
  if (camera instanceof PerspectiveCamera) {
    return fitObjectToPersCamera(object, camera);
  } else if (camera instanceof OrthographicCamera) {
    return fitObjectToOrthoCamera(object, camera);
  }

  return { position: new Vector3(), target: new Vector3() };
}

/**
 * Keeps the historical `legacy-aspect` framing (see {@link FitDistanceMode}).
 * The returned `zoom` is the legacy pseudo-FOV value some callers still read.
 */
export function fitObjectToPersCamera(
  object: Object3D | { center: Vector3; radius: number },
  camera: PerspectiveCamera
) {
  const { center, radius } = boundsOf(object);

  const legacyFov = (camera.aspect || 1) * camera.fov * (Math.PI / 180);
  const distance = computeFitDistance(
    radius,
    camera.fov,
    camera.aspect,
    "legacy-aspect",
  );

  const direction = new Vector3();
  camera.getWorldDirection(direction).multiplyScalar(-distance);

  const newPosition = center.clone().add(direction);

  return { position: newPosition, target: center, zoom: legacyFov };
}

export function fitObjectToOrthoCamera(
  object: Object3D | { center: Vector3; radius: number },
  camera: OrthographicCamera
) {
  const { center, radius } = boundsOf(object);

  const aspect = camera.right / camera.top;
  const newZoom = (aspect < 1.0 ? camera.right : camera.top) / radius;
  camera.zoom = newZoom;

  camera.position.copy(center).add(new Vector3(0, 10, 0));
  camera.updateProjectionMatrix();

  return { position: camera.position.clone(), target: center, zoom: newZoom };
}

/**
 * Orbit hedefini nesne merkezine alır; kamera ofseti (bakış yönü) korunur.
 * Fit öncesi pan kaymasını düzeltmek için.
 */
export function centerCameraOnObject(
  camera: CameraInterface,
  object: Object3D | { center: Vector3; radius: number },
  fallbackOffset = new Vector3(5, 10, 0),
): Vector3 {
  const { center } = boundsOf(object);
  const instance = camera.instance;
  const ctrl = camera.controller as
    | { target: Vector3; update?: () => void }
    | undefined;

  if (ctrl?.target) {
    const offset = instance.position.clone().sub(ctrl.target);
    if (offset.lengthSq() < 1e-10) {
      offset.copy(fallbackOffset);
    }
    ctrl.target.copy(center);
    instance.position.copy(center).add(offset);
    ctrl.update?.();
  } else {
    const direction = new Vector3();
    instance.getWorldDirection(direction);
    const distance = Math.max(instance.position.length(), fallbackOffset.length());
    instance.position.copy(center).addScaledVector(direction, -distance);
    instance.lookAt(center);
  }

  return center;
}

/** Önce merkeze taşı, sonra fit mesafesini uygula. */
export function centerAndFitCamera(
  camera: CameraInterface,
  object: Object3D | { center: Vector3; radius: number },
): { position: Vector3; target: Vector3 } | undefined {
  centerCameraOnObject(camera, object);
  return camera.fit(object);
}
